import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  changeInvitationTypeStatus,
  createInvitationType,
  deleteInvitationType,
  getInvitationTypes,
  reorderInvitationTypes,
  updateInvitationType,
} from '../api/invitationTypeApi'
import type {
  InvitationType,
  InvitationTypeStatus,
  InvitationTypeUpsert,
  PagedResponse,
} from '../types'

const KEY = 'activity-invitation-types'

/**
 * Davet türü yönetimi sunucu-durumu (025): liste + oluştur/güncelle/durum/sil/yeniden-sırala mutation'ları.
 * Her başarıda liste invalidate edilir. Güncel kayıt cache'e yazılır (bayat modDate → 409 önlenir).
 * (023 useEventTypes deseniyle aynı; tüketen akış henüz kapsam dışı.)
 */
export function useInvitationTypes() {
  const { i18n } = useTranslation()
  const qc = useQueryClient()

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [KEY] })
  }

  const applyUpdated = (updated: InvitationType) =>
    qc.setQueryData<PagedResponse<InvitationType>>([KEY, i18n.language], (prev) =>
      prev
        ? { ...prev, content: prev.content.map((t) => (t.id === updated.id ? updated : t)) }
        : prev,
    )

  const list = useQuery({
    queryKey: [KEY, i18n.language],
    queryFn: getInvitationTypes,
  })

  // meta.handledCodes: domaine özel iş kodu ekranda ele alınır → merkezi hata toast'ı (019) susar.
  const create = useMutation({
    mutationFn: (body: InvitationTypeUpsert) => createInvitationType(body),
    onSuccess: invalidate,
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: InvitationTypeUpsert }) =>
      updateInvitationType(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  const changeStatus = useMutation({
    mutationFn: ({ id, body }: { id: number; body: InvitationTypeStatus }) =>
      changeInvitationTypeStatus(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
  })

  const remove = useMutation({
    mutationFn: (id: number) => deleteInvitationType(id),
    onSuccess: invalidate,
  })

  // Yeniden sıralama: liste anında (optimistic) yeni sıraya geçer; backend 1..N yazar. Hata → geri al.
  const reorder = useMutation({
    mutationFn: (orderedIds: number[]) => reorderInvitationTypes(orderedIds),
    onMutate: async (orderedIds) => {
      await qc.cancelQueries({ queryKey: [KEY, i18n.language] })
      const prev = qc.getQueryData<PagedResponse<InvitationType>>([KEY, i18n.language])
      if (prev) {
        const byId = new Map(prev.content.map((r) => [r.id, r]))
        const content = orderedIds
          .map((id) => byId.get(id))
          .filter((r): r is InvitationType => r != null)
        qc.setQueryData<PagedResponse<InvitationType>>([KEY, i18n.language], { ...prev, content })
      }
      return { prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData([KEY, i18n.language], ctx.prev)
    },
    onSettled: invalidate,
  })

  return { list, create, update, changeStatus, remove, reorder }
}
